"use client";

import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import ShareIcon from "@heroicons/react/24/outline/ShareIcon";
import HeartIcon from "@heroicons/react/24/outline/HeartIcon";
import HeartSolidIcon from "@heroicons/react/24/solid/HeartIcon";
import QuestionMarkCircleIcon from "@heroicons/react/24/outline/QuestionMarkCircleIcon";
import PhoneIcon from "@heroicons/react/24/outline/PhoneIcon";
import VenueDetailHeaderInfo from "@/components/views/venue-details/new-details/header/VenueDetailHeaderInfo";
import { useSession } from "next-auth/react";
import { useDataUpdater } from "@/lib/hooks/useApi";
import { useFavorites } from "@/hooks/useFavorites";
import AuthModal from "@/components/views/modals/auth/AuthModal";
import VenueHeaderPhotosCarousel from "@/components/views/venue-details/card/VenueHeaderPhotosCarousel";
import VenueDetailHeaderPhotoSection from "@/components/views/venue-details/new-details/header/photo";
import dynamic from "next/dynamic";
import { useCurrentDeviceContext } from "@/context/DeviceContext";
import { useIsAskQuestionEnabled } from "@/hooks/useIsAskQuestionEnabled";
import { useVenueDetail } from "@/hooks/venueContext";
import { isPhysiotherapistVenue } from "@/lib/venue-business-type";

const ModalGallery = dynamic(
  () => import("@/components/views/venue-details/card/ModalGallery"),
);
const ImageGallery = dynamic(
  () => import("@/components/views/venue-details/card/ImageGallery"),
);
const VenueDetailContactModal = dynamic(
  () => import("@/components/views/venue-details/new-details/ContactModal"),
);
const VenueShareModal = dynamic(
    () => import("@/components/views/modals/venue/ShareModal"),
);

interface props {
  datas: {
    discounts: [
      {
        percent: string | number;
      },
    ];
    likeCount: number;
    phone: string;
    title: string;
    discount_title: string;
    id: number;
    banners: { url: string }[];
    videos: string[];
    location: string;
    tags: { title: string }[];
    logo: string;
  };
  clickAdressLink: string;
  title?: string;
}

export default function VenueDetailHeader(props: props) {
  const { device } = useCurrentDeviceContext();
  const isMobile = device === "mobile";
  const isAskQuestionEnabled = useIsAskQuestionEnabled();
  const { venue } = useVenueDetail();
  // Physiotherapist venues have no comment / question section (SAB-362).
  const showsAskQuestion = isAskQuestionEnabled && !isPhysiotherapistVenue(venue);
  const { data: sessionData, update } = useSession();
  const [slideCount, setSlideCount] = useState(0);
  const [isOpen, setOpen] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [isDisabledLikeButton, setIsDisabledLikeButton] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [windowWith, setWindowWidth] = useState(0);
  const [detailModalIsOpen, setDetailModalIsOpen] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [activeImage, setActiveImage] = useState(1);
  const [show, setShow] = useState(false);

  const { sendRequest: increaseView } = useDataUpdater({
    path: `venue/increase-view/${props.datas.id}`,
    body: {},
    method: "POST",
    auth: false,
  });

  useEffect(() => {
    setWindowWidth(window.innerWidth);
    window.addEventListener("resize", () => setWindowWidth(window.innerWidth));
  }, []);

  useEffect(() => {
    setLikeCount(props.datas.likeCount);
  }, [props.datas]);

  useEffect(() => {
    const likedVenues = sessionData?.user?.likedVenues || [];
    setIsLiked(likedVenues.includes(props.datas.id));
  }, [sessionData]);

  const {
    handleFavoriteClick: addFavorite,
    handleUnFavoriteClick: removeFavorite,
  } = useFavorites(props.datas.id);

  const handleFavoriteClick = async (event: ReactEvents.OnClickEvent) => {
    event.stopPropagation();
    event.preventDefault();

    if (isDisabledLikeButton) return;

    if (!sessionData?.user) {
      setShowLoginModal(true);
    } else {
      setIsLiked(true);
      setIsDisabledLikeButton(true);
      const success = await addFavorite();
      if (!success) {
        setIsLiked(false);
        setIsDisabledLikeButton(false);
        return;
      }

      setLikeCount(likeCount + 1);
      setIsDisabledLikeButton(false);
    }
  };

  const handleUnFavoriteClick = async (event: ReactEvents.OnClickEvent) => {
    event.stopPropagation();
    event.preventDefault();

    if (isDisabledLikeButton) return;

    setIsLiked(false);
    setIsDisabledLikeButton(true);
    const success = await removeFavorite();
    if (!success) {
      setIsLiked(true);
      setIsDisabledLikeButton(false);
      return;
    }

    setLikeCount(likeCount - 1);
    setIsDisabledLikeButton(false);
  };

  useEffect(() => {
    if (props.datas.id) increaseView();
  }, [props.datas.id]);

  return (
    <div className="flex flex-col md:gap-8 md:pb-4 gap-2 md:relative">
      <AuthModal
        initialModal="login"
        isOpened={showLoginModal}
        closer={() => setShowLoginModal(false)}
      />

      {showGallery && (
        <ImageGallery
          venueTitle={props.datas?.title}
          venueId={props.datas.id}
          open={showGallery}
          closer={() => setShowGallery(false)}
          images={props.datas.banners}
        />
      )}

      {show && (
        <ModalGallery
          showContactButtons={false}
          venueTitle={props.datas?.title}
          venueId={props.datas.id}
          show={show}
          activeImageIndex={activeImage}
          closer={() => setShow(false)}
          images={props.datas.banners}
        />
      )}

      {isOpen && (
        <VenueShareModal isOpen={isOpen} onClose={() => setOpen(false)} />
      )}
      {detailModalIsOpen && (
        <VenueDetailContactModal
          addressLink={props.clickAdressLink}
          isOpen={detailModalIsOpen}
          onClose={() => setDetailModalIsOpen(false)}
          includeDistrictSlug={true}
        />
      )}

      {/* Mobile header: rendered only for mobile requests so the desktop header
          below is not duplicated into the same markup. */}
      {isMobile && (
      <div className="w-screen relative left-0">
        {/* Venue name + WhatsApp above the photos */}
        <VenueDetailHeaderInfo variant="mobile" section="title" />

        <div className="h-[282px] relative">
          <div className="absolute inset-0 w-screen">
            <VenueHeaderPhotosCarousel
              venueTitle={props.datas?.title}
              venueId={props.datas.id}
              width={windowWith}
              imageClassNames="h-[282px] object-cover"
              images={props.datas.banners}
              videos={props.datas.videos}
              slideCount={slideCount}
              setSlideCount={(n) => {
                setSlideCount(n);
              }}
              hoverableButtons={false}
              showButtons={false}
              showContactButtons={true}
            />
          </div>
        </div>

        {/* Mobile Header Actions */}
        <VenueDetailHeaderInfo
          variant="mobile"
          section="actions"
          onShareClick={() => setOpen(true)}
          onContactClick={() => setDetailModalIsOpen(true)}
        />
      </div>
      )}

      {/* Desktop Header */}
      {!isMobile && (
      <div className="flex container flex-col gap-4">
        {/* Venue Info Above Photos */}
        <VenueDetailHeaderInfo variant="desktop" />

        <VenueDetailHeaderPhotoSection
          setShow={setShow}
          setActiveImage={setActiveImage}
          setShowGallery={setShowGallery}
          venue={props.datas}
        />

        {/* Action Buttons Below Photos */}
        <div className="flex justify-end items-center">
          <div className="flex gap-3 items-center">
            {/* Heart/Favorite Icon */}
            <button
              disabled={isDisabledLikeButton}
              aria-disabled={isDisabledLikeButton}
              onClick={isLiked ? handleUnFavoriteClick : handleFavoriteClick}
              className={cn(
                "flex gap-1 items-center cursor-pointer",
                isDisabledLikeButton && "cursor-not-allowed",
              )}
            >
              {!isLiked ? (
                <HeartIcon className="size-6 text-gray-400" />
              ) : (
                <HeartSolidIcon className="size-6 text-red-500" />
              )}
              <span
                className="text-center text-gray-400 text-sm font-medium"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {likeCount}
              </span>
            </button>

            {/* Paylaş Button */}
            <button
              onClick={() => setOpen(true)}
              className="flex items-center justify-center gap-2 bg-white border border-[#d9d9d9] rounded-[9px] px-4 py-2 shadow-[0px_0px_2.4px_0px_rgba(99,99,99,0.25)]"
            >
              <ShareIcon className="size-5 text-[#6f6f6f]" />
              <span
                className="text-[14px] font-medium text-[#6f6f6f]"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Paylaş
              </span>
            </button>

            {/* Soru Sorun Button */}
            {showsAskQuestion && (
              <button
                onClick={() => {
                  const element = document.getElementById("comments");
                  if (element) {
                    const isMobile = window.innerWidth < 768;
                    const offset = isMobile ? 100 : 140;
                    const elementPosition =
                      element.getBoundingClientRect().top + window.scrollY;
                    window.scrollTo({
                      top: elementPosition - offset,
                      behavior: "smooth",
                    });
                  }
                }}
                className="flex items-center justify-center gap-2 bg-white border border-[#d9d9d9] rounded-[9px] px-4 py-2 shadow-[0px_0px_2.4px_0px_rgba(99,99,99,0.25)]"
              >
                <QuestionMarkCircleIcon className="size-5 text-[#6f6f6f]" />
                <span
                  className="text-[14px] font-medium text-[#6f6f6f]"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Soru Sorun
                </span>
              </button>
            )}

            {/* İletişim Button */}
            <button
              onClick={() => setDetailModalIsOpen(true)}
              className="flex items-center justify-center gap-2 bg-[#20b038] border border-[#d9d9d9] rounded-[9px] px-4 py-2 shadow-[0px_0px_2.4px_0px_rgba(99,99,99,0.25)]"
            >
              <PhoneIcon className="size-5 text-white" />
              <span
                className="text-[14px] font-medium text-white"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                İletişim
              </span>
            </button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
